import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { CURRENCY } from "../../constants/Variables";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import GlobalButton from "../CustomButton";
import { handleOpenSnackBar } from "../../utils";
import AppLoader from "../AppLoader";
import CustomSwitch from "../CustomSwitch";
import { t } from "i18next";
import { useSelector } from "react-redux";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(3),
  },
  "& .MuiPaper-root": {
    width: 600,
    borderRadius: 12,
    boxShadow: theme.shadows[5],
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(2),
      width: "100%",
    },
  },
}));

const cardElementOptions = {
  style: {
    base: {
      color: "#000000",
      fontFamily: "'Roboto', sans-serif",
      fontSize: "15px",
      fontWeight: "500",
      letterSpacing: "0.025em",
      backgroundColor: "#f9f9f9",
      padding: "10px",
      border: "1px solid #ddd",
      borderRadius: "4px",
      "::placeholder": {
        color: "#888888",
      },
    },
    invalid: {
      color: "#ff5252",
      iconColor: "#ff5252",
    },
  },
  hidePostalCode: true,
};

const SubscriptionPurchaseDialog = forwardRef(({ onPayment }, ref) => {
  const stripe = useStripe();
  const elements = useElements();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toggleVal, setToggleVal] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const { currentLanguage } = useSelector((state) => state.translationReducer);

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      setToggleVal(
        params.subscription.subscriptionType == "yearly" ? true : false
      );
      setDialogData(params);
    },
    closeDialog: () => setOpen(false),
  }));

  const handleClose = () => {
    setOpen(false);
  };

  console.log(dialogData, "dialogDatadialogDatadialogDatadialogDatadialogData");

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      maxWidth={"sm"}
    >
      <AppLoader isVisible={isLoading} />

      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        id="customized-dialog-title"
      >
        <Typography variant="h1" fontWeight={600} fontSize={"1.5rem"}>
          {t("subscription")}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
            "&:hover": {
              backgroundColor: (theme) => theme.palette.action.hover,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Box>
            <CustomSwitch
              label={toggleVal ? t("yearly") : t("monthly")}
              checked={toggleVal}
              onChange={(ee) => setToggleVal(ee.target.checked)}
            />
            <Typography
              variant="h6"
              fontWeight={600}
              textTransform={"capitalize"}
            >
              {`${t(dialogData?.subscription?.name[currentLanguage])} ${t(
                "plan"
              )}`}
            </Typography>
            <Typography
              variant="body2"
              color={"custom.slateGrey"}
              lineHeight={"normal"}
            >
              {t("billed")} {toggleVal ? t("yearly") : t("monthly")}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              textTransform={"capitalize"}
            >
              {`${CURRENCY} ${
                toggleVal
                  ? dialogData?.subscription?.yearlyPrice
                  : dialogData?.subscription?.price
              }`}
            </Typography>
            <Typography
              variant="body2"
              color={"custom.slateGrey"}
              lineHeight={"normal"}
            >
              {toggleVal ? t("yearly") : t("monthly")}
            </Typography>
          </Box>
        </Stack>

        <Typography mt={3} variant="body2" mb={0.5}>
          {t("enterCardDetails")}
        </Typography>
        <Box
          sx={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "10px",
          }}
        >
          <CardElement options={cardElementOptions} />
        </Box>
        <Stack direction={"row"} alignItems={"center"} gap={"5px"} my={0.5}>
          <Typography variant="body2" color={"#6c6f75"}>
            {t("securePayment")}
          </Typography>
          <Typography fontSize={18} fontWeight={600} color={"primary.main"}>
            Stripe
          </Typography>
        </Stack>

        {dialogData?.warningText && (
          <Typography
            mt={2}
            variant="body2"
            fontStyle={"italic"}
            color={"primary"}
          >
            {t("note")}: {dialogData?.warningText}
          </Typography>
        )}
        {dialogData?.staffLimitReached && (
          <Typography
            mt={2}
            variant="body2"
            fontStyle={"italic"}
            color={"primary"}
          >
            {t("staffLimitReached")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <GlobalButton
          btnText={"cancel"}
          handleClickBtn={handleClose}
          variant="outlined"
        />
        <GlobalButton
          btnText={"proceedPayment"}
          handleClickBtn={async () => {
            try {
              setIsLoading(true);
              if (!stripe || !elements) {
                return handleOpenSnackBar("error", t("stripeNotLoaded"));
              }
              const cardElement = elements.getElement(CardElement);
              const { token, error } = await stripe.createToken(cardElement);

              if (error) {
                return handleOpenSnackBar("error", error.message);
              }

              onPayment({
                ...dialogData,
                tokenId: token.id,
                type: toggleVal ? "yearly" : "monthly",
              });
            } catch (error) {
              handleOpenSnackBar("error", error.message);
            } finally {
              setIsLoading(false);
            }
          }}
        />
      </DialogActions>
    </BootstrapDialog>
  );
});

export default SubscriptionPurchaseDialog;
